import { motion } from "framer-motion";
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Truck, Wrench, DollarSign, Hash, Calculator, Ship, Package,
  Play, Clock, CheckCircle2, ArrowLeft, ArrowRight, Sparkles, Shield,
  Cloud, Monitor
} from "lucide-react";
import { LogoIcon } from "@/components/LogoWithText";
import SEO from "@/components/SEO";
import LazyImage from "@/components/LazyImage";
import { useLanguage } from "@/context/LanguageContext";

const systems = [
  {
    id: "shipping",
    name: "Sistema de Envios",
    fullName: "Global Metafora Shipping",
    description: "Plataforma completa de embarque maritimo, rastreo online, recogida gratis y gestion de contenedores USA a RD.",
    features: ["Rastreo en tiempo real", "Gestion de contenedores", "Facturacion automatica", "Portal de clientes", "Recogida a domicilio"],
    demoUrl: "https://globalmetaforademo.com/prueba-gratis",
    demoReady: true,
    icon: Truck,
    color: "#3b82f6",
    image: "/products/envios.jpg",
  },
  {
    id: "lametafora",
    name: "La Metafora Shipping",
    fullName: "La Metafora Shipping Inc",
    description: "Transporte y entrega confiable desde Estados Unidos hasta Republica Dominicana con rastreo de paquetes.",
    features: ["Envio puerta a puerta", "Rastreo de paquetes", "Registro de clientes", "Cotizaciones online", "Ofertas especiales"],
    demoUrl: "https://globalmetaforademo.com/prueba-gratis",
    demoReady: true,
    icon: Ship,
    color: "#2563eb",
    image: "/products/lametafora.jpg",
  },
  {
    id: "metaforashipping",
    name: "Metafora Shipping",
    fullName: "Metafora Shipping Network",
    description: "Envios rapidos y seguros puerta a puerta. Maritimo y aereo con cobertura total en Republica Dominicana.",
    features: ["Envio maritimo y aereo", "Cobertura nacional RD", "10,000+ paquetes enviados", "100% seguro", "20-25 dias entrega"],
    demoUrl: "https://globalmetaforademo.com/prueba-gratis",
    demoReady: true,
    icon: Package,
    color: "#ef4444",
    image: "/products/metaforashipping.jpg",
  },
  {
    id: "taller",
    name: "Sistema de Taller",
    fullName: "Tripitaka - Mecanica General",
    description: "Gestion automotriz completa: ordenes de trabajo, inventario de repuestos, facturacion y portal de clientes.",
    features: ["Ordenes de trabajo", "Inventario repuestos", "Portal clientes", "Facturacion", "Galeria de trabajos"],
    demoUrl: "https://demo-taller.globalmetafora.com/api/prueba-gratis",
    demoReady: true,
    icon: Wrench,
    color: "#ef4444",
    image: "/products/taller.jpg",
  },
  {
    id: "prestamos",
    name: "Sistema de Prestamos",
    fullName: "Inversion Metafora",
    description: "Administra tu cartera de prestamos. Control total sobre clientes, cobros, reportes y gestion financiera.",
    features: ["Gestion de prestamos", "Cobros automaticos", "Reportes financieros", "Control de clientes", "Historial de pagos"],
    demoUrl: "https://inversionmetafora.com",
    demoReady: true,
    icon: DollarSign,
    color: "#10b981",
    image: "/products/prestamos.jpg",
  },
  {
    id: "loteria",
    name: "Sistema de Loteria",
    fullName: "Metafora Lottery System",
    description: "Control de bancas, sorteos, premios y agentes. Reportes en tiempo real y liquidacion automatica.",
    features: ["Control de bancas", "Gestion de sorteos", "Liquidacion automatica", "Reportes en tiempo real", "Red de agentes"],
    demoUrl: "https://demo-loteria.globalmetafora.com/api/prueba-gratis",
    demoReady: true,
    icon: Hash,
    color: "#f59e0b",
    image: null,
  },
  {
    id: "contabilidad",
    name: "Sistema de Contabilidad",
    fullName: "Metafora Accounting",
    description: "Gestion financiera completa: balance general, estados de resultados, cuentas por cobrar/pagar.",
    features: ["Balance general", "Estados financieros", "Cuentas por cobrar", "Cuentas por pagar", "Reportes fiscales"],
    demoUrl: null,
    demoReady: false,
    icon: Calculator,
    color: "#8b5cf6",
    image: null,
  },
];

const SystemCard = ({ system }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { t } = useLanguage();
  const Icon = system.icon;

  return (
    <motion.div
      data-testid={`demo-card-${system.id}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="relative rounded-2xl overflow-hidden border border-white/5 bg-[#0a0f1e] hover:border-white/10 transition-all duration-500 group"
    >
      <div className="flex flex-col md:flex-row h-full">
        {/* Image side */}
        <div className="relative w-full md:w-2/5 h-48 md:h-auto overflow-hidden">
          {system.image ? (
            <LazyImage
              src={system.image}
              alt={system.name}
              className="w-full h-full object-cover object-top"
              animate={{ scale: isHovered ? 1.05 : 1 }}
              transition={{ duration: 0.5 }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#0a0f1e] to-[#111827] min-h-[200px]">
              <Icon size={56} style={{ color: `${system.color}40` }} />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0a0f1e] hidden md:block" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0f1e] to-transparent md:hidden" />

          <div className="absolute top-3 left-3">
            <span
              className="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider flex items-center gap-1"
              style={{
                backgroundColor: system.demoReady ? "#10b98125" : "#f59e0b20",
                border: `1px solid ${system.demoReady ? "#10b98150" : "#f59e0b40"}`,
                color: system.demoReady ? "#10b981" : "#f59e0b",
              }}
            >
              <CheckCircle2 size={10} />
              {system.demoReady ? t("demos_available") : t("demos_coming_soon")}
            </span>
          </div>
        </div>

        {/* Content side */}
        <div className="flex-1 p-6 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${system.color}15`, border: `1px solid ${system.color}30` }}
              >
                <Icon size={20} style={{ color: system.color }} />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">{system.name}</h3>
                <p className="text-[#64748b] text-xs">{system.fullName}</p>
              </div>
            </div>

            <p className="text-[#94a3b8] text-sm leading-relaxed my-4">
              {system.description}
            </p>

            {/* Version Cloud / Local */}
            <div className="mb-4 space-y-1.5" data-testid={`demo-versions-${system.id}`}>
              <div className="flex items-start gap-2">
                <Cloud size={14} className="text-[#06b6d4] mt-0.5 shrink-0" />
                <p className="text-xs leading-snug">
                  <span className="text-[#06b6d4] font-semibold">{t("version_cloud")}</span>
                  <span className="text-[#64748b]"> {t("version_cloud_desc")}</span>
                </p>
              </div>
              <div className="flex items-start gap-2">
                <Monitor size={14} className="text-[#10b981] mt-0.5 shrink-0" />
                <p className="text-xs leading-snug">
                  <span className="text-[#10b981] font-semibold">{t("version_local")}</span>
                  <span className="text-[#64748b]"> {t("version_local_desc")}</span>
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-5">
              {system.features.map((f, i) => (
                <span
                  key={i}
                  className="px-2.5 py-1 rounded-md text-[11px] font-medium bg-white/5 text-[#94a3b8] border border-white/5"
                >
                  {f}
                </span>
              ))}
            </div>
          </div>

          {system.demoReady ? (
            <motion.a
              data-testid={`demo-start-${system.id}`}
              href={system.demoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full py-3 rounded-xl font-bold text-sm text-center flex items-center justify-center gap-2 text-white"
              style={{ backgroundColor: system.color }}
              whileHover={{ scale: 1.02, boxShadow: `0 0 20px ${system.color}40` }}
              whileTap={{ scale: 0.98 }}
            >
              <Play size={14} />
              {t("demos_try_free")}
            </motion.a>
          ) : (
            <span
              data-testid={`demo-start-${system.id}`}
              className="w-full py-3 rounded-xl font-bold text-sm text-center flex items-center justify-center gap-2 border text-[#64748b]"
              style={{ borderColor: `${system.color}30` }}
            >
              Disponible Pronto
            </span>
          )}
        </div>
      </div>

      <motion.div
        className="absolute bottom-0 left-0 right-0 h-[2px]"
        style={{ background: `linear-gradient(90deg, transparent, ${system.color}, transparent)` }}
        initial={{ opacity: 0 }}
        animate={{ opacity: isHovered ? 1 : 0 }}
      />
    </motion.div>
  );
};

const DemosPage = () => {
  const { t } = useLanguage();
  return (
    <div className="min-h-screen bg-[#02040a]">
      <SEO
        title="Demos Gratuitas - Global Metafora APP"
        description="Prueba gratis por 15 dias todos los sistemas de Global Metafora APP."
      />

      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-[#02040a]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="demos-nav-logo">
            <LogoIcon size={36} />
            <div>
              <span className="text-white font-bold tracking-wide text-sm block leading-tight">
                GLOBAL <span className="text-[#3b82f6]">METAFORA</span>
              </span>
              <span className="text-[#3b82f6]/60 text-[9px] font-semibold tracking-[0.2em]">APP</span>
            </div>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-[#94a3b8] hover:text-white text-sm transition-colors"
          >
            <ArrowLeft size={16} />
            Volver al inicio
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-28 pb-16 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#3b82f6]/5 blur-[150px] rounded-full" />
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10 text-center">
          <motion.div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#10b981]/20 bg-[#10b981]/5 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Shield size={14} className="text-[#10b981]" />
            <span className="text-[#10b981] text-xs font-bold uppercase tracking-wider">
              15 Dias Gratis &middot; Sin Tarjeta de Credito
            </span>
          </motion.div>

          <motion.h1
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-4 tracking-tight"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            {t("demos_title_1")}{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#3b82f6] to-[#06b6d4]">
              {t("demos_title_2")}
            </span>
            {" "}{t("demos_title_3")}
          </motion.h1>

          <motion.p
            className="text-[#94a3b8] text-base sm:text-lg max-w-2xl mx-auto mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            {t("demos_subtitle")}
          </motion.p>

          <motion.div
            className="flex items-center justify-center gap-6 text-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <span className="text-[#10b981] font-semibold flex items-center gap-1">
              <CheckCircle2 size={14} /> {systems.filter(s => s.demoReady).length} {t("demos_active")}
            </span>
            <span className="text-[#f59e0b] font-semibold flex items-center gap-1">
              <Clock size={14} /> {systems.filter(s => !s.demoReady).length} {t("demos_coming")}
            </span>
          </motion.div>
        </div>
      </section>

      {/* Systems list */}
      <section className="pb-24 px-6">
        <div className="max-w-5xl mx-auto space-y-6">
          {systems.map((system) => (
            <SystemCard key={system.id} system={system} />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div className="rounded-2xl border border-white/5 bg-[#0a0f1e] p-10">
            <h2 className="text-2xl font-bold text-white mb-3">
              {t("demos_not_found")}
            </h2>
            <p className="text-[#94a3b8] text-sm mb-6">
              {t("demos_custom")}
            </p>
            <Link
              to="/#contacto"
              className="inline-flex items-center gap-2 px-8 py-3 rounded-xl bg-[#3b82f6] text-white font-bold text-sm hover:bg-[#2563eb] transition-colors"
            >
              {t("demos_contact_btn")}
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DemosPage;
